import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleAlertIcon, FileTextIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { useForm, useWatch, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';

import { brainsEndpoints } from '@/api/endpoints';
import FileUpload from '@/components/atoms/FileUpload/FileUpload';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  Form as FormRoot,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useBrains from '@/hooks/useBrains';
import { LLMSelector } from '@/modules/aiAgents/components/LLMSelector';
import { DEFAULT_FACTUALITY_SENSITIVITY } from '@/modules/aiAgents/constants';
import { getAIAgentType } from '@/modules/aiAgents/helper';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { languageByCountryCode } from '@/shared/languageCodes';
import { Brain, BrainLanguage, BrainType } from '@/shared/types/brain';
import { UploadedFile } from '@/shared/types/server';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';
import { extractNames, generateUniqueName } from '@/util/util';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { updateBrainsUrls } from '../helpers';
import { Form } from './ModalAIAgentCreate';

type FormWithDefaults = Form & { provider: string; language_model_id: string };

const RHF_SETVALUE_CONFIG = { shouldValidate: true, shouldDirty: true } as const;

const ACCEPT_TYPES = { 'application/json': ['.json'] } as const;

type ImportAIAgentFormProps = { setStep: Dispatch<SetStateAction<number>> };

/**
 * Validation schema for Import AI Agent form
 * Matches the validation pattern used in Step2 (template-based flow)
 */
const createImportFormSchema = (t: (key: string, options?: { 0: number }) => string) =>
  yup
    .object()
    .shape({
      brain_type: yup.string(),
      name: createNameFieldSchema({ getEndpoint: brainsEndpoints.checkName }),
      language: yup.string().required(t('validation.required')),
      provider: yup.string(),
      language_model_id: yup.string(),
    });

export const ImportAIAgentForm = ({ setStep }: ImportAIAgentFormProps) => {
  // Redux selectors
  const slug = useSelector(selectAccountSlug);

  // Custom hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { brains, createBrain } = useBrains();
  const navigate = useNavigate();

  // Local state
  const [fileUploadError, setFileUploadError] = useState<string | null>('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [jsonLanguage, setJsonLanguage] = useState<BrainLanguage>(null);
  const [importedBrain, setImportedBrain] = useState<Partial<Brain> | null>(null);

  // Validation schema
  const importFormSchema = useMemo(() => createImportFormSchema(t), [t]);

  // RHF
  const form = useForm<FormWithDefaults>({
    resolver: yupResolver(importFormSchema) as Resolver<FormWithDefaults>,
    mode: 'onChange',
    defaultValues: { provider: 'moveo', language_model_id: 'Default' },
  });
  const {
    setValue,
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = form;

  // Local variables
  const isDisabled = !isDirty || !isValid || !importedBrain;
  // Derived, not stored: warn when the picked language differs from the JSON's.
  const language = useWatch({ control, name: 'language' });
  const showBanner = jsonLanguage !== null && language !== jsonLanguage;
  const languageOptions = useMemo(
    () =>
      prioritizeLanguageOptions(
        Object.keys(languageByCountryCode).map((i) => ({
          value: i.toLowerCase(),
          label: formatLanguageLabel(i),
        }))
      ),
    []
  );

  const hasUploadedFile = !!importedBrain;
  const message = useMemo(
    () => (
      <span className="flex flex-col items-center gap-2">
        <UploadIcon size={16} strokeWidth={1.25} className="text-foreground" />
        <span className="text-sm leading-6 font-medium text-foreground">
          {t('file_upload.dnd_replace_prefix')}{' '}
          <span className="text-primary">{t('file_upload.dnd_replace_browse')}</span>
          {hasUploadedFile && <> {t('file_upload.dnd_replace_suffix')}</>}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('file_upload.supported_file_types', { 0: '.json' })}
        </span>
      </span>
    ),
    [t, hasUploadedFile]
  );

  // Callbacks
  const updateForm = useCallback(
    (name: string, language: BrainLanguage) => {
      const uniqueName = generateUniqueName(name, extractNames(brains));

      // Validate and mark the fields as dirty
      // to activate submit button
      setValue('name', uniqueName, RHF_SETVALUE_CONFIG);
      setValue('language', language, RHF_SETVALUE_CONFIG);

      setJsonLanguage(language);
    },
    [brains, setValue]
  );

  const validateForm = useCallback(
    (data: Partial<Brain>) => {
      const { name, language } = data;

      // Display error when JSON is not valid
      if (!name || !language) {
        setFileUploadError(t('errors.invalid_json'));
        toast.error(t('errors.invalid_json_format'), {
          description: t('errors.invalid_json_hint'),
          duration: 6000,
          classNames: { icon: '!items-start !self-start' },
        });
        return false;
      }

      // Clear any previous error once a valid file is provided.
      setFileUploadError(null);
      updateForm(name, language);

      return true;
    },
    [t, updateForm]
  );

  const onJsonFileUpload = useCallback(
    async ({ data, name }: UploadedFile) => {
      const updatedBrain = updateBrainsUrls(data);

      if (validateForm(updatedBrain)) {
        setImportedBrain(updatedBrain);
        setFileName(name);
      } else {
        setImportedBrain(null);
        setFileName(null);
      }
    },
    [validateForm]
  );

  const removeFile = useCallback(() => {
    setImportedBrain(null);
    setFileName(null);
    setFileUploadError(null);
  }, []);

  // An unparseable file is also surfaced as a generic "Invalid JSON file" error.
  const handleUploadError = useCallback(() => {
    setImportedBrain(null);
    setFileName(null);
    setFileUploadError(t('errors.invalid_json'));
  }, [t]);

  const onSubmit = async (data: Form) => {
    if (data.language_model_id === '' || data.language_model_id === 'Default') {
      data.language_model_id = null;
    }

    createBrain(
      {
        ...importedBrain,
        ...data,
        brain_type: importedBrain.brain_type as BrainType,
        name: data.name,
        language: data.language,
        guidelines: {
          ...importedBrain?.guidelines,
          factuality_sensitivity:
            importedBrain?.guidelines?.factuality_sensitivity || DEFAULT_FACTUALITY_SENSITIVITY,
        },
      },
      {
        onSuccess: (brain) => {
          dispatch(popModal());
          navigate(`/${slug}/ai-agents/${brain.brain_id}`);
        },
      }
    );
  };

  const handleBackClick = () => {
    if (importedBrain) {
      removeFile();
    }
    setStep((prev) => prev - 1);
  };
  return (
    <FormRoot {...form}>
      <form
        autoComplete="off"
        className="flex h-full flex-col gap-6"
        id="import-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="mt-10">
          <div className="h-[132px] [&>section]:m-0">
            <FileUpload
              message={message}
              onFileUploaded={onJsonFileUpload}
              parseJson
              accept={ACCEPT_TYPES}
              dropzoneOnly
              onUploadError={handleUploadError}
            />
          </div>

          {fileUploadError && <p className="mt-1 text-sm text-warning">{fileUploadError}</p>}

          {!fileUploadError && fileName && (
            <div className="group mt-2 flex items-center justify-between gap-2 rounded-md px-1 py-1 hover:bg-muted/50">
              <span className="flex min-w-0 items-center gap-1 text-xs leading-4 font-medium text-muted-foreground">
                <FileTextIcon size={16} className="shrink-0" />
                <span className="truncate">{fileName}</span>
              </span>
              <Button
                type="button"
                variant="ghost-neutral"
                size="icon-sm"
                onClick={removeFile}
                aria-label={t('common.remove')}
                className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive focus-visible:opacity-100 hover:[&_svg]:text-destructive"
              >
                <Trash2Icon size={16} />
              </Button>
            </div>
          )}
        </div>

        {importedBrain && (
          <Badge flavour="informational" rounded={false} className="leading-4 font-semibold">
            {getAIAgentType(importedBrain.brain_type)}
          </Badge>
        )}

        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('common.name')}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('common.name_placeholder')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="language"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('field.language')}</FormLabel>
              <FormControl>
                <PopoverCombobox
                  options={languageOptions}
                  value={field.value ?? importedBrain?.language ?? ''}
                  onChange={field.onChange}
                  triggerPlaceholder={t('common.select_an_option')}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <LLMSelector form={form} />

        {showBanner && (
          <Alert variant="warning" className="gap-y-2">
            <CircleAlertIcon size={16} />
            <AlertTitle className="text-sm leading-5 font-semibold tracking-normal">
              {t('brains.language_mismatch.title')}
            </AlertTitle>
            <AlertDescription>{t('brains.language_mismatch.message')}</AlertDescription>
          </Alert>
        )}

        <footer className="mt-auto flex justify-end">
          <div className="flex w-full gap-2 [&>:nth-last-child(2)]:ml-auto">
            <Button onClick={handleBackClick} variant="ghost" type="button">
              {t('common.back')}
            </Button>

            <Button variant="ghost" onClick={() => dispatch(popModal())}>
              {t('common.cancel')}
            </Button>
            <Button disabled={isDisabled} type="submit">
              {t('common.create')}
            </Button>
          </div>
        </footer>
      </form>
    </FormRoot>
  );
};
